import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail, User, ArrowRight, Sparkles } from 'lucide-react';
import Logo from '../components/Logo';
import SunsetBackground from '../components/ui/SunsetBackground';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Credentials verified!');
        // Navigation is handled by App.js based on twoFAState
      } else {
        await register(email, password, name);
        toast.success('Account created! Set up 2FA to continue.');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <SunsetBackground />
      
      {/* Left Panel - Hero */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.95) 0%, rgba(184, 150, 46, 0.95) 50%, rgba(139, 112, 37, 0.95) 100%)'
        }}
      >
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 rounded-xl bg-white/20 border border-white/30">
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" fill="currentColor" />
                <circle cx="12" cy="5" r="1.8" fill="currentColor" opacity="0.9" />
                <circle cx="12" cy="19" r="1.8" fill="currentColor" opacity="0.9" />
                <circle cx="5" cy="12" r="1.8" fill="currentColor" opacity="0.9" />
                <circle cx="19" cy="12" r="1.8" fill="currentColor" opacity="0.9" />
                <line x1="12" y1="9" x2="12" y2="6.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="12" y1="15" x2="12" y2="17.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="9" y1="12" x2="6.8" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="15" y1="12" x2="17.2" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              MyAiAnalysis
            </span>
          </div>
          
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            AI-Powered<br />
            Database<br />
            Intelligence
          </h1>
          
          <p className="text-lg text-white/80 mb-8 max-w-md">
            Ask natural language questions about your data. Get instant insights, SQL queries, and actionable analytics.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-2 h-2 rounded-full bg-white" />
              <span>Connect multiple MySQL databases</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-2 h-2 rounded-full bg-white" />
              <span>Claude AI powered analysis</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-2 h-2 rounded-full bg-white" />
              <span>Role-based access control</span>
            </div>
          </div>
        </div>
        
        {/* Decorative circles */}
        <div className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full bg-white/10" />
        <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-white/10" />
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl border-white/40 shadow-2xl shadow-amber-900/20" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center mb-4">
              <Logo size="default" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {isLogin ? 'Welcome back' : 'Create account'}
            </CardTitle>
            <CardDescription className="text-gray-500">
              {isLogin 
                ? 'Sign in to access your analytics dashboard' 
                : 'Get started with your database intelligence platform'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#D4AF37] focus:bg-white"
                      required={!isLogin}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#D4AF37] focus:bg-white"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#D4AF37] focus:bg-white"
                    required
                    minLength={6}
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full btn-gold font-medium py-5"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Please wait...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{isLogin ? 'Sign in' : 'Create account'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-gray-500 hover:text-[#D4AF37] transition-colors"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <span className="text-[#D4AF37] font-medium">
                  {isLogin ? 'Sign up' : 'Sign in'}
                </span>
              </button>
            </div>
            
            {/* Feature highlight */}
            <div className="mt-8 p-4 rounded-lg bg-gradient-to-r from-[#D4AF37]/10 to-[#F4E5A3]/20 border border-[#D4AF37]/20">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Powered by Claude AI</p>
                  <p className="text-xs text-gray-500">Ask questions in plain English</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
